import { useId, useState } from 'react';
import { Box, Dialog, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import clientAdminAddUserForm from '../../../assets/Support/Training/client-admin-add-user-form.png';
import clientAdminUserManagement from '../../../assets/Support/Training/client-admin-user-management.png';
import clientAdminUserManagementNavigation from '../../../assets/Support/Training/client-admin-user-management-navigation.png';
import mainDashboard from '../../../assets/Support/Training/main-dashboard.png';
import userAccessLoginScreen from '../../../assets/Support/Training/user-access-login-screen.png';
import userAccessVerification from '../../../assets/Support/Training/user-access-verification.png';
import containerOrderingNavigation from '../../../assets/Support/Training/container-ordering-1.png';
import containerHistoryNavigation from '../../../assets/Support/Training/container-history-1.png';
import containerHistoryMenu from '../../../assets/Support/Training/container-history-2.png';
import containerOrderingClientSelection from '../../../assets/Support/Training/container-ordering-client-selection.png';
import placeAnOrderButton from '../../../assets/Support/Training/place-an-order-button.png';
import orderList from '../../../assets/Support/Training/order-list.png';
import containerSelectionFilters from '../../../assets/Support/Training/container-selection-1.png';
import containerSelectionResults from '../../../assets/Support/Training/container-selection-2.png';
import individualContainerSelection from '../../../assets/Support/Training/individual-container-selection.png';
import individualContainerUnitType from '../../../assets/Support/Training/individual-container-unit-type.png';
import groupedSets from '../../../assets/Support/Training/grouped-sets.png';
import finaliseOrderShoppingCart from '../../../assets/Support/Training/finalise-order-shopping-cart.png';
import finaliseOrderSummary from '../../../assets/Support/Training/finalise-order-summary.png';
import finaliseOrderPickup from '../../../assets/Support/Training/finalise-order-pickup-1.png';
import reviewConfirmOrder from '../../../assets/Support/Training/review-confirm-order.png';
import finaliseOrderDelivery from '../../../assets/Support/Training/finalise-order-delivery-1.png';
import defaultAddress from '../../../assets/Support/Training/defaultAddress.png';
import settings from '../../../assets/Support/Training/settings.png';
import confirmationMessage from '../../../assets/Support/Training/confirmation-message.png';
import status from '../../../assets/Support/Training/status.png';

interface SupportImageProps {
    image: string;
    alt: string;
}

export const SupportImage = ({ image, alt }: SupportImageProps) => {
    const [imageUnavailable, setImageUnavailable] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [fittedWidth, setFittedWidth] = useState<number>();
    const viewerTitleId = useId();
    const assetImages: Record<string, string> = {
        'container-ordering-1.png': containerOrderingNavigation,
        'container-history-1.png': containerHistoryNavigation,
        'container-history-2.png': containerHistoryMenu,
        'defaultAddress.png': defaultAddress,
        'container-ordering-client-selection.png': containerOrderingClientSelection,
        'place-an-order-button.png': placeAnOrderButton,
        'order-list.png': orderList,
        'container-selection-1.png': containerSelectionFilters,
        'container-selection-2.png': containerSelectionResults,
        'individual-container-selection.png': individualContainerSelection,
        'individual-container-unit-type.png': individualContainerUnitType,
        'grouped-sets.png': groupedSets,
        'finalise-order-shopping-cart.png': finaliseOrderShoppingCart,
        'finalise-order-summary.png': finaliseOrderSummary,
        'finalise-order-pickup-1.png': finaliseOrderPickup,
        'review-confirm-order.png': reviewConfirmOrder,
        'finalise-order-delivery-1.png': finaliseOrderDelivery,
        'settings.png': settings,
        'confirmation-message.png': confirmationMessage,
        'status.png': status,
        'client-admin-add-user-form.png': clientAdminAddUserForm,
        'client-admin-user-management.png': clientAdminUserManagement,
        'client-admin-user-management-navigation.png': clientAdminUserManagementNavigation,
        'main-dashboard.png': mainDashboard,
        'user-access-login-screen.png': userAccessLoginScreen,
        'user-access-verification.png': userAccessVerification,
    };
    const src = assetImages[image] ?? `${process.env.PUBLIC_URL}/support/${image}`;

    return (
        <Box component="figure" sx={{ m: 0, my: 1.5, width: '100%', minWidth: 0, maxWidth: 760 }}>
            {imageUnavailable ? (
                <Box
                    role="img"
                    aria-label={`${alt} image unavailable`}
                    sx={{
                        minHeight: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        border: '1px dashed #9aa8b5',
                        borderRadius: 1,
                        bgcolor: '#f7f9fb',
                        p: 2,
                        textAlign: 'center',
                    }}
                >
                    <Typography sx={{ color: '#003b7a', fontWeight: 600, fontSize: '16px' }}>{alt}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                        Image unavailable.
                    </Typography>
                </Box>
            ) : (
                <Box component="button" type="button" aria-label={`Magnify image: ${alt}`} aria-haspopup="dialog" onClick={() => { setZoom(1); setFittedWidth(undefined); setViewerOpen(true); }} sx={{ display: 'block', position: 'relative', width: 'fit-content', maxWidth: '100%', p: 0, border: 0, bgcolor: 'transparent', borderRadius: 1, cursor: 'zoom-in', '&:hover .support-image-zoom, &:focus-visible .support-image-zoom': { opacity: 1 }, '&:focus-visible': { outline: '2px solid #003b7a', outlineOffset: 3 } }}>
                <Box
                    component="img"
                    src={src}
                    alt={alt}
                    onError={() => setImageUnavailable(true)}
                    sx={{
                        display: 'block',
                        width: 'auto',
                        height: 'auto',
                        maxWidth: '100%',
                        maxHeight: { xs: 280, md: 420 },
                        objectFit: 'contain',
                        boxSizing: 'border-box',
                        border: '1px solid #d5dbe1',
                        borderRadius: 1,
                    }}
                />
                <Box component="span" className="support-image-zoom" aria-hidden="true" sx={{ opacity: 0, position: 'absolute', right: 6, bottom: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', color: '#003b7a', border: '1px solid #d5dbe1', borderRadius: 1, pointerEvents: 'none' }}><ZoomInIcon fontSize="small" /></Box>
                </Box>
            )}
            <Dialog open={viewerOpen} onClose={() => setViewerOpen(false)} aria-labelledby={viewerTitleId} fullWidth maxWidth="lg" PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' } } }}>
                <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 1 }, p: 1.5, borderBottom: '1px solid #dce3e8' }}>
                    <Typography component="h2" id={viewerTitleId} sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 700, color: '#003b7a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{alt}</Typography>
                    <Tooltip title="Zoom out"><span><IconButton aria-label="Zoom out" disabled={zoom <= 0.5} onClick={() => setZoom((current) => Math.max(0.5, current - 0.25))}><ZoomOutIcon /></IconButton></span></Tooltip>
                    <Typography component="span" role="status" aria-live="polite" sx={{ width: 42, flexShrink: 0, textAlign: 'center', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{Math.round(zoom * 100)}%</Typography>
                    <Tooltip title="Zoom in"><span><IconButton aria-label="Zoom in" disabled={zoom >= 4} onClick={() => setZoom((current) => Math.min(4, current + 0.25))}><ZoomInIcon /></IconButton></span></Tooltip>
                    <Tooltip title="Reset zoom"><IconButton aria-label="Reset zoom" onClick={() => setZoom(1)}><RestartAltIcon /></IconButton></Tooltip>
                    <Tooltip title="Close image"><IconButton aria-label="Close image" onClick={() => setViewerOpen(false)}><CloseIcon /></IconButton></Tooltip>
                </DialogTitle>
                <Box tabIndex={0} role="region" aria-label="Zoomed image" sx={{ height: '70vh', minHeight: 240, overflow: 'auto', p: 2, bgcolor: '#f4f6f8' }}>
                    <Box component="img" src={src} alt={alt} onLoad={(event) => {
                        const loadedImage = event.currentTarget;
                        const viewport = loadedImage.parentElement;
                        if (viewport) setFittedWidth(Math.min(loadedImage.naturalWidth, viewport.clientWidth - 32, (viewport.clientHeight - 32) * loadedImage.naturalWidth / loadedImage.naturalHeight));
                    }} sx={{ display: 'block', width: fittedWidth ? fittedWidth * zoom : 'auto', maxWidth: fittedWidth ? 'none' : '100%', height: 'auto', mx: 'auto', bgcolor: '#fff' }} />
                </Box>
            </Dialog>
        </Box>
    );
};
